import { AsyncPipe, NgClass, NgFor, NgIf, NgOptimizedImage, getCurrencySymbol } from '@angular/common';
import { Component, DestroyRef, EventEmitter, HostBinding, Input, OnChanges, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { MoneyService } from '@frontend/sports/common/core/feature/money';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { SportsJackpotBannerViewModel } from '@frontend/sports/types/components/banners/models';
import { UserService } from '@frontend/sports/user/feature';
import { NativeAppService } from '@frontend/vanilla/core';
import { Observable } from 'rxjs';

import { ArcRestrictionService } from '../arc/arc-restriction.service';
import { RedirectHelperService } from '../navigation-core/redirect-helper.service';
import { UrlHelperService } from '../navigation-core/url-helper.service';
import { BannerTickComponent } from './banner-tick.component';
import { BannerTrackingService } from './banner-tracking.service';
import { JackpotEvent, JackpotIncrementEvent, JackpotRefreshEvent, TickModel } from './banner.model';
import { JackpotService } from './jackpot.service';

@Component({
    selector: 'ms-banner-jackpot',
    templateUrl: 'banner-jackpot.component.html',
    imports: [NgIf, NgOptimizedImage, NgFor, NgClass, BannerTickComponent, AsyncPipe],
})
export class BannerJackpotComponent implements OnInit, OnDestroy, OnChanges {
    @Input() model: SportsJackpotBannerViewModel;
    @Input() position = '';
    @Input() listIndex: number;
    @Output() bannerDataLoaded = new EventEmitter<boolean>();
    @HostBinding('class') className = 'banner-jackpot';

    link: string;
    characters: TickModel[] = [];
    backgroundImage?: string;
    private value: number;
    hideGamingCrossSellByArc$: Observable<boolean>;
    private readonly destroyRef = inject(DestroyRef);
    constructor(
        private jackpotService: JackpotService,
        private userService: UserService,
        private currency: MoneyService,
        private trackingService: BannerTrackingService,
        private urlHelper: UrlHelperService,
        private nativeAppService: NativeAppService,
        private redirectHelper: RedirectHelperService,
        private arcRestrictionService: ArcRestrictionService,
    ) {
        this.hideGamingCrossSellByArc$ = this.arcRestrictionService.gamingCrossSellEnabled$;
    }

    ngOnChanges(changes: ISimpleChanges<BannerJackpotComponent>): void {
        if (changes.model?.currentValue.value) {
            this.update(changes.model.currentValue.value);
        }
    }

    ngOnInit(): void {
        this.jackpotService.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(this.onEvent);
        if (this.model.jackpotGroup) {
            this.jackpotService.registerGroup(this.model.jackpotGroup);
        }
    }

    ngOnDestroy(): void {
        if (this.model.jackpotGroup) {
            this.jackpotService.unregisterGroup(this.model.jackpotGroup);
        }
    }

    trackByFn(index: any, item: any): string {
        return index;
    }

    onJackpotBannerClick(): void {
        if (this.model.title) {
            this.trackingService.trackBannerClickCrossSell(this.model.title, this.position, this.link);
        }

        this.redirectHelper.goToPage(this.link);
    }

    private onEvent = (event: JackpotEvent) => {
        let newValue: number | undefined;

        if (event instanceof JackpotIncrementEvent) {
            newValue = event.incrementBy ? this.value + event.incrementBy : undefined;
        } else if (event instanceof JackpotRefreshEvent && this.model.jackpotGroup) {
            newValue = event.newValues[this.model.jackpotGroup];
        }

        if (newValue !== undefined) {
            this.update(newValue);
        }
    };

    private update = (value: number) => {
        this.value = value;

        const formattedValue = this.currency.transform(value);

        if (!formattedValue) {
            this.bannerDataLoaded.emit(false);

            return;
        }

        const currencySymbol = getCurrencySymbol(this.userService.currency, 'narrow');

        this.link = this.getBannerLink();
        this.characters = this.createModel(formattedValue, currencySymbol);
        this.backgroundImage = this.model.background;
        this.bannerDataLoaded.emit(true);
    };

    private createModel(formattedValue: string, currencySymbol: string): TickModel[] {
        const characters: TickModel[] = currencySymbol.split('').map((character) => ({ isCurrency: true, currency: character }));

        formattedValue.split('').forEach((character) => {
            if (this.isNumber(character)) {
                characters.push({ isDigit: true, digit: parseInt(character) });
            } else {
                characters.push({ isSeparator: true, separator: character });
            }
        });

        return characters;
    }

    private getBannerLink(): string {
        const href = this.model.link?.href;

        if (!href) {
            return '#';
        }

        if (href.startsWith('/')) {
            return this.urlHelper.getUrl(href);
        }

        if (this.model.link) {
            this.model.link.target = this.nativeAppService.isNative ? '_self' : this.model.link.target || '_self';
        }

        return href;
    }

    private isNumber(character: string): boolean {
        return '0123456789'.indexOf(character) !== -1;
    }
}
